import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { CheckIcon, CommentIcon, XIcon } from '../components/icons';
import { cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { REACTION_META } from '../lib/reactions';
import { REACTION_KINDS, type ReactionKind } from '../lib/types';
import type { ReactionsState } from './useReactions';

/**
 * The pill under the video: the reactions, a divider, and Comment.
 *
 * Glyphs only, with the word in the label for screen readers and the
 * tooltip. Tapping one never pauses, never seeks, and never moves focus off
 * the video.
 *
 * Comment turns the pill itself into the composer: the reactions drop away
 * and the bubble opens into a text field, pinned to the moment Comment was
 * pressed. It works the same in full screen, where the side panel is out of
 * sight.
 */
export function ReactionPill({
  state,
  mediaRef,
  ready,
  onPostComment,
  compact = false,
  guard,
}: {
  state: ReactionsState;
  mediaRef: RefObject<HTMLMediaElement | null>;
  ready: boolean;
  /** Posts a comment from the pill; omitted and there is no Comment button. */
  onPostComment?: (bodyText: string, timecodeSec: number | null) => Promise<unknown>;
  /** Inside the end-of-video card: no frame, no Comment button. */
  compact?: boolean;
  /** Runs the reaction, or asks the visitor to sign in first. */
  guard?: (purpose: 'react' | 'comment', then: () => void) => void;
}) {
  // The moment the comment belongs to, or null while the reactions show.
  const [composingAt, setComposingAt] = useState<number | null>(null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const composing = composingAt !== null;

  useEffect(() => {
    if (composing) inputRef.current?.focus();
  }, [composing]);

  // The field grows with its lines, up to about five, then scrolls.
  useEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    if (!composing) {
      field.style.height = '';
      return;
    }
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, 128)}px`;
  }, [body, composing]);

  useEffect(() => {
    if (!sent) return;
    const timer = window.setTimeout(() => setSent(false), 2000);
    return () => window.clearTimeout(timer);
  }, [sent]);

  if (state.unavailable) return null;

  const openComposer = () => {
    const at = mediaRef.current?.currentTime ?? 0;
    const go = () => {
      setSendError(null);
      setComposingAt(at);
    };
    if (guard) guard('comment', go);
    else go();
  };

  const closeComposer = () => {
    setComposingAt(null);
    setBody('');
    setSendError(null);
  };

  const send = (event: FormEvent) => {
    event.preventDefault();
    const text = body.trim();
    if (!text || !onPostComment || sending) return;
    setSending(true);
    setSendError(null);
    onPostComment(text, composingAt !== null && composingAt > 0 ? Math.floor(composingAt) : null)
      .then(() => {
        closeComposer();
        setSent(true);
      })
      .catch((error: unknown) => setSendError(errorMessage(error)))
      .finally(() => setSending(false));
  };

  const react = (kind: ReactionKind) => {
    // The moment is read now, before any prompt, so a reaction that waits
    // for a sign-in still lands where the tap was.
    const at = mediaRef.current?.currentTime ?? 0;
    const go = () => state.react(kind, at);
    if (guard) guard('react', go);
    else go();
  };

  const buttons = REACTION_KINDS.map((kind) => {
    const meta = REACTION_META[kind];
    const count = state.totals[kind];
    return (
      <button
        key={kind}
        type="button"
        onClick={() => react(kind)}
        disabled={!ready}
        title={meta.label}
        aria-label={count > 0 ? `${meta.label}, ${count} so far` : meta.label}
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'Spacebar') event.stopPropagation();
        }}
        className={cx(
          'relative flex h-10 w-10 items-center justify-center rounded-full text-[20px] leading-none transition-transform duration-120',
          'hover:scale-110 hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none',
          state.lastAccepted === kind && 'scale-110 bg-slate-800',
        )}
      >
        <span aria-hidden="true">{meta.glyph}</span>
        {count > 0 ? (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-slate-700 px-1 text-center text-micro tnum text-chalk"
          >
            {count}
          </span>
        ) : null}
      </button>
    );
  });

  if (compact) return <div className="flex items-center justify-center gap-1">{buttons}</div>;

  // Both halves are grid tracks that animate between 0fr and 1fr, so the
  // pill resizes smoothly around whichever one is showing.
  const track =
    'grid transition-[grid-template-columns,opacity,transform] duration-300 ease-out motion-reduce:transition-none';

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        data-hold-controls={composing ? '' : undefined}
        className={cx(
          'flex items-end border border-hairline bg-slate-900 px-2 py-1.5 shadow-lg shadow-black/30',
          // Full round would clip a field that has grown to several lines.
          composing ? 'rounded-[1.5rem]' : 'rounded-full',
        )}
      >
        <div
          aria-hidden={composing}
          inert={composing}
          className={cx(
            track,
            composing ? 'translate-y-4 grid-cols-[0fr] opacity-0' : 'grid-cols-[1fr] opacity-100',
          )}
        >
          <div className="flex min-w-0 items-center gap-1 overflow-hidden">
            {buttons}
            {onPostComment ? <span aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-hairline" /> : null}
          </div>
        </div>

        {onPostComment ? (
          <form onSubmit={send} className="flex items-end">
            <div className={cx(track, composing ? 'grid-cols-[1fr] opacity-100' : 'grid-cols-[0fr] opacity-0')}>
              <div className="flex min-w-0 items-center overflow-hidden">
                <textarea
                  rows={1}
                  ref={inputRef}
                  value={body}
                  maxLength={4000}
                  tabIndex={composing ? 0 : -1}
                  disabled={!composing}
                  aria-label="Comment"
                  placeholder="Leave a comment…"
                  onChange={(event) => setBody(event.target.value)}
                  onKeyDown={(event) => {
                    // Enter sends; Shift+Enter is a new line.
                    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                      event.preventDefault();
                      event.currentTarget.form?.requestSubmit();
                      return;
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      event.stopPropagation();
                      closeComposer();
                    }
                  }}
                  onBlur={(event) => {
                    // Empty and left behind: the reactions come back. Moving
                    // to the pill's own buttons is not leaving.
                    if (event.currentTarget.form?.contains(event.relatedTarget as Node | null)) return;
                    if (!body.trim()) closeComposer();
                  }}
                  className="block h-10 w-[min(24rem,60vw)] shrink-0 resize-none bg-transparent px-2 py-2 text-body leading-6 focus-visible:shadow-none text-chalk placeholder:text-chalk-faint focus:outline-none"
                />
              </div>
            </div>

            {composing ? (
              <>
                <button
                  type="submit"
                  disabled={!body.trim() || sending}
                  className="flex h-10 shrink-0 items-center rounded-full px-3 text-body text-chalk hover:bg-slate-800 disabled:opacity-40"
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
                <button
                  type="button"
                  onClick={closeComposer}
                  aria-label="Cancel comment"
                  title="Cancel"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-chalk-dim hover:bg-slate-800 hover:text-chalk"
                >
                  <XIcon size={16} />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={openComposer}
                disabled={!ready}
                className="flex h-10 shrink-0 items-center gap-2 rounded-full px-3 text-body text-chalk hover:bg-slate-800 disabled:opacity-40"
              >
                {sent ? <CheckIcon size={16} /> : <CommentIcon size={16} />}
                {sent ? 'Sent' : 'Comment'}
              </button>
            )}
          </form>
        ) : null}
      </div>
      {sendError ?? state.postError ? (
        <p role="alert" className="text-caption text-danger">
          {sendError ?? state.postError}
        </p>
      ) : null}
    </div>
  );
}
